import { useState } from "react";

// ─── Break & Rest Data ─────────────────────────────────────────────────────────
// meal: null = no state requirement beyond federal
// meal: { after: hours trigger, dur: minutes (null = unspecified), paid: bool, waivable: bool, note: string, second: {...} }
// rest: null = no state requirement beyond federal
// rest: { per: hours between breaks, dur: minutes (null = unspecified), paid: bool, note: string }
// minor: null = follow adult rules (federal child labor laws still apply)
// minor: override object for states with stronger minor protections than adult rules

const BD = {
  AL: { name:"Alabama",    adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Alabama child labor law requires a 30-minute break for minors after 5 consecutive hours." }, stateNote:"Alabama has no state meal or rest break requirements for adults. Federal FLSA applies." },
  AK: { name:"Alaska",     adult:{ meal:null, rest:null }, minor:null, stateNote:"Alaska has no state meal or rest break requirements. Federal FLSA applies. Minors are protected by Alaska child labor statutes." },
  AZ: { name:"Arizona",    adult:{ meal:null, rest:null }, minor:null, stateNote:"Arizona has no state meal or rest break requirements. Federal FLSA applies." },
  AR: { name:"Arkansas",   adult:{ meal:null, rest:null }, minor:null, stateNote:"Arkansas has no state meal or rest break requirements. Federal FLSA applies." },
  CA: { name:"California", adult:{
    meal:{ after:5, dur:30, paid:false, waivable:true, waiveNote:"Waivable by mutual written consent if shift is 6 hours or less. A second 30-minute meal break is required after 10 hours (waivable if ≤12 hr shift and first meal not waived).", second:{ after:10, dur:30, paid:false } },
    rest:{ per:4, dur:10, paid:true, note:"One 10-minute paid rest break per 4 hours worked, or major fraction thereof (>2 hrs). Employees working 3.5–7.5 hrs: 1 break. 7.5–11.5 hrs: 2 breaks." }
  }, minor:null, stateNote:"⚠ California penalties: 1 additional hour of pay per missed meal break AND 1 additional hour per missed rest break. These are among the most litigated wage claims in the country." },
  CO: { name:"Colorado",   adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:{ per:4, dur:10, paid:true, note:"Rest periods count as compensable work time. Employees working 4–8 hrs: 1 break. 8–12 hrs: 2 breaks." }
  }, minor:null, stateNote:"Colorado's COMPS Order #38 governs break requirements for most employees." },
  CT: { name:"Connecticut",adult:{
    meal:{ after:7.5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Connecticut requires a 30-minute unpaid break for shifts over 7.5 consecutive hours." },
  DE: { name:"Delaware",   adult:{
    meal:{ after:7.5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Delaware requires a 30-minute meal period for adult employees working 7.5+ consecutive hours." },
  DC: { name:"Washington D.C.", adult:{ meal:null, rest:null }, minor:null, stateNote:"D.C. follows federal FLSA for break requirements. No additional break mandates beyond federal law." },
  FL: { name:"Florida",    adult:{ meal:null, rest:null }, minor:{ meal:{ after:4, dur:30, paid:false }, rest:null, note:"Florida law requires a 30-minute break for minors under 18 after 4 consecutive hours." }, stateNote:"Florida has no state break requirements for adult employees. Federal FLSA applies." },
  GA: { name:"Georgia",    adult:{ meal:null, rest:null }, minor:null, stateNote:"Georgia has no state meal or rest break requirements. Federal FLSA applies." },
  HI: { name:"Hawaii",     adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Hawaii requires a 30-minute unpaid meal break after 5 consecutive hours of work." },
  ID: { name:"Idaho",      adult:{ meal:null, rest:null }, minor:null, stateNote:"Idaho has no state meal or rest break requirements. Federal FLSA applies." },
  IL: { name:"Illinois",   adult:{
    meal:{ after:7.5, dur:20, paid:false, note:"Must be scheduled approximately at the midpoint of the shift." },
    rest:null
  }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Illinois requires a 30-minute break for minors after 5 consecutive hours — more protective than the adult standard." }, stateNote:"Illinois One Day Rest In Seven Act (ODRISA) governs meal breaks. Note: Illinois Workplace Transparency Act applies to harassment notice requirements." },
  IN: { name:"Indiana",    adult:{ meal:null, rest:null }, minor:null, stateNote:"Indiana has no state meal or rest break requirements for adults. Federal FLSA applies." },
  IA: { name:"Iowa",       adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Iowa requires a 30-minute break for minors under 16 working 5+ hours." }, stateNote:"Iowa has no state break requirements for adults. Federal FLSA applies." },
  KS: { name:"Kansas",     adult:{ meal:null, rest:null }, minor:null, stateNote:"Kansas has no state meal or rest break requirements. Federal FLSA applies." },
  KY: { name:"Kentucky",   adult:{
    meal:{ after:8, dur:null, paid:false, note:"Reasonable off-duty period close to midpoint of shift. Exact duration not specified by statute." },
    rest:{ per:4, dur:10, paid:true }
  }, minor:null, stateNote:"Kentucky KRS 337.365 requires a 10-minute paid rest period per 4 hours worked and a reasonable meal period for shifts over 8 hours." },
  LA: { name:"Louisiana",  adult:{ meal:null, rest:null }, minor:null, stateNote:"Louisiana has no state meal or rest break requirements. Federal FLSA applies." },
  ME: { name:"Maine",      adult:{
    meal:{ after:6, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Maine requires a 30-minute rest period after 6 consecutive hours of work. Employees must be completely relieved of duties." },
  MD: { name:"Maryland",   adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Maryland requires a 30-minute break for employees under 18 after 5 consecutive hours. Adults have no state-mandated break requirement." }, stateNote:"Maryland has no meal or rest break requirements for adult employees. Federal FLSA applies." },
  MA: { name:"Massachusetts", adult:{
    meal:{ after:6, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Massachusetts MGL c.149 §100 requires a 30-minute meal break after 6 hours. The Attorney General actively enforces this requirement." },
  MI: { name:"Michigan",   adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"Michigan requires a 30-minute unpaid meal period after 5 consecutive hours of work." },
  MN: { name:"Minnesota",  adult:{
    meal:{ after:8, dur:null, paid:false, note:"Sufficient time to eat — statute does not specify exact minutes. Typically interpreted as 20–30 minutes." },
    rest:{ per:4, dur:null, paid:true, note:"Adequate rest in restroom facilities every 4 hours. Statute does not specify minutes — typically 10 minutes in practice." }
  }, minor:null, stateNote:"Minnesota Minn. Stat. §177.253–.254 requires both meal time and rest breaks. Note: Minnesota's Earned Sick and Safe Time law has additional recordkeeping requirements." },
  MS: { name:"Mississippi", adult:{ meal:null, rest:null }, minor:null, stateNote:"Mississippi has no state meal or rest break requirements. Federal FLSA applies." },
  MO: { name:"Missouri",   adult:{ meal:null, rest:null }, minor:null, stateNote:"Missouri has no state meal or rest break requirements for adults. Federal FLSA applies." },
  MT: { name:"Montana",    adult:{ meal:null, rest:null }, minor:null, stateNote:"Montana has no state meal or rest break requirements. Federal FLSA applies." },
  NE: { name:"Nebraska",   adult:{ meal:null, rest:null }, minor:null, stateNote:"Nebraska has no state meal or rest break requirements. Federal FLSA applies." },
  NV: { name:"Nevada",     adult:{
    meal:{ after:8, dur:30, paid:false, note:"If an employee works 3.5–8 hours, a 30-min meal break is also required (NRS 608.019)." },
    rest:{ per:4, dur:10, paid:true }
  }, minor:null, stateNote:"Nevada NRS 608.019 requires a 30-minute unpaid meal break after 8 hours AND a 10-minute paid rest break per 4 hours. Note: for shifts of 3.5–8 hours, one 30-min meal break is still required." },
  NH: { name:"New Hampshire", adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"New Hampshire RSA 275:30-a requires a 30-minute lunch period after more than 5 consecutive hours." },
  NJ: { name:"New Jersey", adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"New Jersey requires a 30-minute break for minors under 18 after 5 consecutive hours." }, stateNote:"New Jersey has no state break requirements for adults. Federal FLSA applies." },
  NM: { name:"New Mexico", adult:{ meal:null, rest:null }, minor:null, stateNote:"New Mexico has no state meal or rest break requirements. Federal FLSA applies." },
  NY: { name:"New York",   adult:{
    meal:{ after:6, dur:30, paid:false, note:"Factory workers: 60-minute midday break required. Mercantile/most other industries: 30 minutes after 6 hours. Shifts starting before 11 AM and ending after 7 PM: additional 20-minute break between 5–7 PM." },
    rest:null
  }, minor:null, stateNote:"New York NYLL §162 sets industry-specific break rules. Hospitality employers should also review the NY Hospitality Industry Wage Order for additional requirements." },
  NC: { name:"North Carolina", adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"North Carolina requires a 30-minute break for employees under 16 after 5 consecutive hours." }, stateNote:"North Carolina has no state break requirements for adults. Federal FLSA applies." },
  ND: { name:"North Dakota", adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:null
  }, minor:null, stateNote:"North Dakota requires a 30-minute unpaid meal break after 5 consecutive hours." },
  OH: { name:"Ohio",       adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Ohio requires a 30-minute break for minors under 18 after 5 consecutive hours." }, stateNote:"Ohio has no state break requirements for adult employees. Federal FLSA applies." },
  OK: { name:"Oklahoma",   adult:{ meal:null, rest:null }, minor:null, stateNote:"Oklahoma has no state meal or rest break requirements. Federal FLSA applies." },
  OR: { name:"Oregon",     adult:{
    meal:{ after:6, dur:30, paid:false },
    rest:{ per:4, dur:10, paid:true, note:"10-minute paid rest for every 4 hours worked or major fraction thereof (>2 hours)." }
  }, minor:null, stateNote:"Oregon ORS 653.261 requires both meal and rest breaks. Rest periods are treated as hours worked. Note: Oregon's predictive scheduling law (ORS 653.480) may also apply to food service employers in Portland." },
  PA: { name:"Pennsylvania", adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Pennsylvania requires a 30-minute break for minors under 18 after 5 consecutive hours." }, stateNote:"Pennsylvania has no state break requirements for adults. Federal FLSA applies." },
  RI: { name:"Rhode Island", adult:{
    meal:{ after:6, dur:20, paid:false },
    rest:null
  }, minor:null, stateNote:"Rhode Island RIGL §28-3-14 requires a 20-minute meal break after 6 consecutive hours of work." },
  SC: { name:"South Carolina", adult:{ meal:null, rest:null }, minor:null, stateNote:"South Carolina has no state meal or rest break requirements. Federal FLSA applies." },
  SD: { name:"South Dakota", adult:{ meal:null, rest:null }, minor:null, stateNote:"South Dakota has no state meal or rest break requirements. Federal FLSA applies." },
  TN: { name:"Tennessee",  adult:{
    meal:{ after:6, dur:30, paid:false, note:"Applies to employers with 5 or more employees." },
    rest:null
  }, minor:null, stateNote:"Tennessee TCA §50-2-103 requires a 30-minute unpaid meal break after 6 hours for employers with 5+ employees." },
  TX: { name:"Texas",      adult:{ meal:null, rest:null }, minor:null, stateNote:"Texas has no state meal or rest break requirements. Federal FLSA applies." },
  UT: { name:"Utah",       adult:{ meal:null, rest:null }, minor:null, stateNote:"Utah has no state meal or rest break requirements. Federal FLSA applies." },
  VT: { name:"Vermont",    adult:{
    meal:{ after:null, dur:30, paid:false, note:"Reasonable meal period (not to exceed 30 min) as close to midpoint of shift as possible. No specific hour trigger defined in statute." },
    rest:null
  }, minor:null, stateNote:"Vermont 21 V.S.A. §304 requires a reasonable meal period near the midpoint of the work period. Vermont courts typically interpret this as applicable to all shifts of meaningful duration." },
  VA: { name:"Virginia",   adult:{ meal:null, rest:null }, minor:{ meal:{ after:5, dur:30, paid:false }, rest:null, note:"Virginia requires a 30-minute break for employees under 16 after 5 consecutive hours." }, stateNote:"Virginia has no state break requirements for adult employees. Federal FLSA applies." },
  WA: { name:"Washington", adult:{
    meal:{ after:5, dur:30, paid:false },
    rest:{ per:4, dur:10, paid:true }
  }, minor:null, stateNote:"Washington WAC 296-126-092 requires a 30-minute unpaid meal break after 5 hours and a 10-minute paid rest break per 4 hours. Both are strictly enforced by L&I." },
  WV: { name:"West Virginia", adult:{
    meal:{ after:6, dur:20, paid:false },
    rest:null
  }, minor:null, stateNote:"West Virginia WV Code §21-3-10a requires a 20-minute meal break for shifts of 6 or more hours." },
  WI: { name:"Wisconsin",  adult:{
    meal:{ after:6, dur:30, paid:false, note:"Must be scheduled close to usual mealtime or at the midpoint of the shift." },
    rest:null
  }, minor:null, stateNote:"Wisconsin DWD 274.02 recommends (and employers must generally provide) a 30-minute meal period for shifts of 6+ hours." },
  WY: { name:"Wyoming",    adult:{ meal:null, rest:null }, minor:null, stateNote:"Wyoming has no state meal or rest break requirements. Federal FLSA applies." },
};

const stateList = Object.entries(BD)
  .sort((a,b) => a[1].name.localeCompare(b[1].name))
  .map(([code, d]) => ({ code, name: d.name }));

// ─── Helpers ───────────────────────────────────────────────────────────────────
function mealTiming(meal, shift) {
  if (!meal) return null;
  if (meal.after == null) return true; // Vermont-style, unspecified trigger
  return shift >= meal.after;
}

function restCount(rest, shift) {
  if (!rest || !rest.per) return 0;
  return Math.floor(shift / rest.per);
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <div style={{ fontFamily:"'Syne', sans-serif", fontWeight:700, fontSize:14,
      letterSpacing:"0.1em", textTransform:"uppercase", color:"#475569", marginBottom:18 }}>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:600, letterSpacing:"0.08em",
        textTransform:"uppercase", color:"#94A3B8", marginBottom:6,
        fontFamily:"'IBM Plex Mono', monospace" }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize:11, color:"#64748B", marginTop:4, fontFamily:"'IBM Plex Mono', monospace" }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width:"100%", padding:"10px 14px", background:"#0D1321", border:"1.5px solid #2D3748",
  borderRadius:8, color:"#F1F5F9", fontSize:15, fontFamily:"'IBM Plex Mono', monospace",
  outline:"none", boxSizing:"border-box", appearance:"none",
};
const selectStyle = { ...inputStyle, cursor:"pointer",
  backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center", paddingRight:36 };

function ResultCard({ icon, label, status, children }) {
  const cfg = {
    required: { bg:"rgba(239,68,68,0.08)", border:"rgba(239,68,68,0.25)", lc:"#EF4444" },
    not_required: { bg:"rgba(16,185,129,0.08)", border:"rgba(16,185,129,0.25)", lc:"#10B981" },
    warn: { bg:"rgba(245,158,11,0.08)", border:"rgba(245,158,11,0.25)", lc:"#F59E0B" },
    info: { bg:"rgba(99,102,241,0.08)", border:"rgba(99,102,241,0.25)", lc:"#818CF8" },
    federal: { bg:"rgba(100,116,139,0.08)", border:"rgba(100,116,139,0.25)", lc:"#94A3B8" },
  }[status] || { bg:"rgba(100,116,139,0.08)", border:"rgba(100,116,139,0.25)", lc:"#94A3B8" };

  return (
    <div style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:12,
      padding:"18px 20px", marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <div style={{ fontFamily:"'Syne', sans-serif", fontWeight:700, fontSize:14,
          color:cfg.lc, letterSpacing:"0.02em" }}>{label}</div>
      </div>
      <div style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:13,
        color:"#CBD5E1", lineHeight:1.7 }}>{children}</div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function BreakRestLookup() {
  const [selState, setSelState] = useState("");
  const [ageGroup, setAgeGroup] = useState("adult");
  const [shiftHours, setShiftHours] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const stateData = selState ? BD[selState] : null;

  const handleCheck = () => {
    setError("");
    if (!stateData) { setError("Please select a state."); return; }
    const shift = parseFloat(shiftHours);
    if (isNaN(shift) || shift <= 0 || shift > 24) { setError("Enter a valid shift length (0.5 – 24 hours)."); return; }

    // Determine which rules apply
    const isMinor = ageGroup !== "adult";
    const isYouth = ageGroup === "minor1415";
    const rules = (isMinor && stateData.minor) ? stateData.minor : stateData.adult;
    const meal = rules?.meal || null;
    const rest = rules?.rest || null;
    const minorNote = isMinor && stateData.minor?.note ? stateData.minor.note : null;

    // Compute meal result
    const mealNeeded = mealTiming(meal, shift);
    const numRest = restCount(rest, shift);

    setResult({ shift, isMinor, isYouth, meal, rest, mealNeeded, numRest, minorNote, sd: stateData });
    setTimeout(() => document.getElementById("blr-results")?.scrollIntoView({ behavior:"smooth", block:"start" }), 50);
  };

  const sectionStyle = { background:"#141B2D", border:"1px solid #1E293B", borderRadius:14,
    padding:"24px 24px 20px", marginBottom:16 };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        select option { background: #141B2D; }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
        input[type=number] { -moz-appearance: textfield; }
        .blr-btn { transition: all 0.18s; }
        .blr-btn:hover { background: #D97706 !important; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(245,158,11,0.35) !important; }
        .blr-btn:active { transform: translateY(0); }
      `}</style>
      <div style={{ minHeight:"100vh", background:"#0D1321", padding:"0 0 60px", fontFamily:"'Syne', sans-serif" }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg, #0D1321 0%, #141B2D 100%)",
          borderBottom:"1px solid #1E293B", padding:"36px 32px 28px" }}>
          <div style={{ maxWidth:720, margin:"0 auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:8, height:8, background:"#F59E0B", borderRadius:"50%" }}/>
              <span style={{ fontSize:11, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase",
                color:"#F59E0B", fontFamily:"'IBM Plex Mono', monospace" }}>
                All 50 States + D.C. · 2026 Edition
              </span>
            </div>
            <h1 style={{ fontFamily:"'Syne', sans-serif", fontSize:32, fontWeight:800, color:"#F8FAFC",
              margin:"0 0 10px", lineHeight:1.15 }}>
              Meal & Rest Break<br/>Requirements Lookup
            </h1>
            <p style={{ color:"#64748B", fontSize:14, margin:0,
              fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
              Select your state, employee age group, and shift length. Get the exact break requirements — what's required, when, how long, and whether it's paid.
            </p>
          </div>
        </div>

        <div style={{ maxWidth:720, margin:"0 auto", padding:"28px 32px 0" }}>

          {/* Section 1 */}
          <div style={sectionStyle}>
            <SectionTitle>① State & Employee</SectionTitle>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <Field label="State">
                <select style={selectStyle} value={selState} onChange={e => { setSelState(e.target.value); setResult(null); }}>
                  <option value="">Select state…</option>
                  {stateList.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Employee Age Group" hint="Minor rules are often more protective">
                <select style={selectStyle} value={ageGroup} onChange={e => { setAgeGroup(e.target.value); setResult(null); }}>
                  <option value="adult">Adult (18 and over)</option>
                  <option value="minor1617">Minor — Age 16–17</option>
                  <option value="minor1415">Minor — Age 14–15</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Section 2 */}
          <div style={sectionStyle}>
            <SectionTitle>② Shift Length</SectionTitle>
            <Field label="Shift Length (hours)" hint="Enter total hours worked in one continuous shift (e.g. 8 or 5.5)">
              <input type="number" style={{ ...inputStyle, maxWidth:240 }}
                step="0.5" min="0.5" max="24"
                placeholder="e.g. 8"
                value={shiftHours}
                onChange={e => { setShiftHours(e.target.value); setResult(null); }}
              />
            </Field>
          </div>

          {error && <div style={{ color:"#EF4444", fontSize:13, marginBottom:12, fontFamily:"'IBM Plex Mono', monospace" }}>{error}</div>}

          <button className="blr-btn" onClick={handleCheck}
            style={{ width:"100%", padding:"16px", background:"#F59E0B", border:"none", borderRadius:12,
              color:"#0D1321", fontSize:16, fontWeight:800, fontFamily:"'Syne', sans-serif",
              cursor:"pointer", letterSpacing:"0.04em", boxShadow:"0 4px 16px rgba(245,158,11,0.25)" }}>
            LOOK UP REQUIREMENTS →
          </button>

          {/* Results */}
          {result && (
            <div id="blr-results" style={{ marginTop:28 }}>

              {/* Verdict banner */}
              <div style={{ background:"rgba(245,158,11,0.08)", border:"2px solid rgba(245,158,11,0.4)",
                borderRadius:16, padding:"20px 28px", marginBottom:20 }}>
                <div style={{ fontFamily:"'Syne', sans-serif", fontWeight:800, fontSize:22,
                  color:"#F59E0B", letterSpacing:"0.03em", marginBottom:4 }}>
                  {result.sd.name} — {result.shift}-Hour Shift
                </div>
                <div style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:13, color:"#94A3B8" }}>
                  {result.isYouth ? "Minor (Age 14–15)" : result.isMinor ? "Minor (Age 16–17)" : "Adult (18+)"} · Break requirements below
                </div>
              </div>

              {/* Meal break */}
              {result.meal ? (
                result.mealNeeded ? (
                  <ResultCard icon="🍽" label="Meal Break: REQUIRED" status="required">
                    {result.meal.dur
                      ? <><strong style={{ color:"#F1F5F9" }}>{result.meal.dur}-minute {result.meal.paid ? "paid" : "unpaid"} meal break required</strong>{result.meal.after ? ` — must be provided before ${result.meal.after} consecutive hours are completed.` : "."}</>
                      : <><strong style={{ color:"#F1F5F9" }}>Meal period required</strong> — duration not specified in statute. Provide a reasonable break (typically 20–30 minutes) near the midpoint of the shift.</>
                    }
                    {result.meal.note && <div style={{ marginTop:8, color:"#94A3B8" }}>📌 {result.meal.note}</div>}
                    {result.meal.waivable && <div style={{ marginTop:8, color:"#94A3B8" }}>↩ Waivable: {result.meal.waiveNote}</div>}
                    {result.meal.second && result.shift >= result.meal.second.after && (
                      <div style={{ marginTop:8, padding:"10px 14px", background:"rgba(239,68,68,0.08)", borderRadius:8, color:"#FCA5A5" }}>
                        ⚠ Second meal break also required — shift exceeds {result.meal.second.after} hours. Provide an additional {result.meal.second.dur}-minute {result.meal.second.paid ? "paid" : "unpaid"} break.
                      </div>
                    )}
                  </ResultCard>
                ) : (
                  <ResultCard icon="🍽" label="Meal Break: Not Yet Required" status="not_required">
                    A {result.meal.dur || 30}-minute {result.meal.paid ? "paid" : "unpaid"} meal break is required after <strong style={{ color:"#F1F5F9" }}>{result.meal.after} hours</strong>. This shift ({result.shift} hrs) does not yet trigger the requirement.
                    {result.meal.after && result.shift < result.meal.after && (
                      <div style={{ marginTop:6, color:"#94A3B8" }}>Add {(result.meal.after - result.shift).toFixed(1)} more hours to this shift and a break becomes mandatory.</div>
                    )}
                  </ResultCard>
                )
              ) : (
                <ResultCard icon="🍽" label="Meal Break: No State Requirement" status="not_required">
                  <strong style={{ color:"#F1F5F9" }}>{result.sd.name} has no state-mandated meal break for this employee type.</strong> Federal FLSA applies — if you choose to provide a meal break, it must be at least 30 minutes and the employee must be completely relieved of duties for it to be unpaid.
                </ResultCard>
              )}

              {/* Rest break */}
              {result.rest ? (
                result.numRest > 0 ? (
                  <ResultCard icon="⏱" label={`Rest Breaks: ${result.numRest} REQUIRED`} status="required">
                    <strong style={{ color:"#F1F5F9" }}>
                      {result.numRest} × {result.rest.dur ? `${result.rest.dur}-minute` : "adequate"} {result.rest.paid ? "paid" : "unpaid"} rest break{result.numRest > 1 ? "s" : ""} required
                    </strong> for a {result.shift}-hour shift (1 break per {result.rest.per} hours worked).
                    {result.rest.paid && <div style={{ marginTop:6, color:"#94A3B8" }}>💰 Rest breaks are <strong style={{ color:"#10B981" }}>compensable work time</strong> — employees must be paid for this time.</div>}
                    {result.rest.note && <div style={{ marginTop:8, color:"#94A3B8" }}>📌 {result.rest.note}</div>}
                  </ResultCard>
                ) : (
                  <ResultCard icon="⏱" label="Rest Breaks: Not Yet Required" status="not_required">
                    <strong style={{ color:"#F1F5F9" }}>{result.sd.name} requires {result.rest.dur || 10}-minute {result.rest.paid ? "paid" : "unpaid"} rest breaks every {result.rest.per} hours.</strong> This shift ({result.shift} hrs) does not yet trigger a required break.
                    {result.rest.paid && <div style={{ marginTop:6, color:"#94A3B8" }}>When provided, rest breaks of 20 minutes or less must always be paid under federal FLSA.</div>}
                  </ResultCard>
                )
              ) : (
                <ResultCard icon="⏱" label="Rest Breaks: No State Requirement" status="not_required">
                  <strong style={{ color:"#F1F5F9" }}>{result.sd.name} has no state-mandated rest breaks.</strong> Under federal FLSA, if you provide short breaks (under 20 minutes), they must be paid as compensable work time — even though they are not legally required.
                </ResultCard>
              )}

              {/* Federal baseline — always shown */}
              <ResultCard icon="🇺🇸" label="Federal FLSA Baseline (Applies in All States)" status="federal">
                <div style={{ display:"grid", gap:6 }}>
                  <div>• <strong style={{ color:"#F1F5F9" }}>Short breaks (&lt;20 min):</strong> Must be paid as work time if provided — regardless of state law.</div>
                  <div>• <strong style={{ color:"#F1F5F9" }}>Meal breaks (30+ min):</strong> May be unpaid only if the employee is completely and genuinely relieved of all duties.</div>
                  <div>• <strong style={{ color:"#F1F5F9" }}>No federal mandate:</strong> FLSA does not require employers to provide any breaks — state law governs minimums.</div>
                  {result.isMinor && <div>• <strong style={{ color:"#F59E0B" }}>Child labor:</strong> Federal FLSA restricts hours and hazardous work for employees under 18, separate from break requirements.</div>}
                </div>
              </ResultCard>

              {/* Minor-specific */}
              {result.isMinor && result.minorNote && (
                <ResultCard icon="👤" label="Minor-Specific Rule in This State" status="warn">
                  {result.minorNote}
                  {result.isYouth && (
                    <div style={{ marginTop:8 }}>
                      ⚠ <strong style={{ color:"#F1F5F9" }}>Age 14–15 federal restrictions also apply:</strong> Max 3 hrs on school days, 8 hrs non-school days. No work before 7 AM or after 7 PM (9 PM in summer). Cannot work during school hours.
                    </div>
                  )}
                </ResultCard>
              )}
              {result.isYouth && !result.minorNote && (
                <ResultCard icon="👤" label="Age 14–15 Federal Child Labor Restrictions" status="warn">
                  <div style={{ display:"grid", gap:4 }}>
                    <div>• Max <strong style={{ color:"#F1F5F9" }}>3 hours on a school day</strong>, 18 hours in a school week</div>
                    <div>• Max <strong style={{ color:"#F1F5F9" }}>8 hours on a non-school day</strong>, 40 hours in a non-school week</div>
                    <div>• No work <strong style={{ color:"#F1F5F9" }}>before 7 AM or after 7 PM</strong> (9 PM between June 1 – Labor Day)</div>
                    <div>• Cannot work during school hours</div>
                    <div>• Many hazardous tasks prohibited — including operating cooking equipment with open flames</div>
                  </div>
                </ResultCard>
              )}

              {/* State note */}
              {result.sd.stateNote && (
                <ResultCard icon="📋" label={`${result.sd.name} — State Law Note`} status="info">
                  {result.sd.stateNote}
                </ResultCard>
              )}

              {/* Affiliate CTA */}
              <div style={{ background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.2)",
                borderRadius:14, padding:"20px 22px", marginBottom:16, display:"flex", gap:16, alignItems:"flex-start" }}>
                <div style={{ fontSize:24, flexShrink:0, marginTop:2 }}>🗓</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"'Syne', sans-serif", fontWeight:700, fontSize:14,
                    color:"#F1F5F9", marginBottom:6 }}>
                    Managing breaks manually across a full schedule is an audit risk.
                  </div>
                  <div style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12,
                    color:"#94A3B8", lineHeight:1.7, marginBottom:12 }}>
                    Payroll and scheduling platforms like Gusto flag break violations automatically, track minor scheduling restrictions, and keep compliant time records in case of a DOL or state labor board inquiry.
                  </div>
                  <a href="https://gusto.pxf.io/X4nKG5" target="_blank" rel="noopener noreferrer sponsored"
                    style={{ display:"inline-block", padding:"9px 18px", background:"#F59E0B",
                      borderRadius:8, color:"#0D1321", fontSize:12, fontWeight:700,
                      fontFamily:"'Syne', sans-serif", textDecoration:"none", letterSpacing:"0.03em" }}>
                    See how Gusto handles scheduling compliance →
                  </a>
                  <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color:"#475569", marginLeft:10 }}>Sponsored</span>
                </div>
              </div>

              {/* Quick reference table */}
              <div style={sectionStyle}>
                <SectionTitle>Quick Reference Summary</SectionTitle>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12 }}>
                  {[
                    ["Shift Length", `${result.shift} hrs`],
                    ["Meal Breaks", result.meal && result.mealNeeded ? `${result.meal.dur || "—"} min ${result.meal.paid ? "(paid)" : "(unpaid)"}` : "Not required"],
                    ["Rest Breaks", result.rest && result.numRest > 0 ? `${result.numRest}× ${result.rest.dur || "—"} min ${result.rest.paid ? "(paid)" : "(unpaid)"}` : "Not required"],
                  ].map(([k,v]) => (
                    <div key={k} style={{ background:"#0D1321", borderRadius:10, padding:"14px 16px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:"#64748B", fontFamily:"'IBM Plex Mono', monospace",
                        letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:6 }}>{k}</div>
                      <div style={{ fontSize:15, fontWeight:700, color:"#F59E0B",
                        fontFamily:"'IBM Plex Mono', monospace" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Disclaimer */}
              <div style={{ padding:"16px 20px", background:"#141B2D", border:"1px solid #1E293B",
                borderRadius:12, marginTop:4 }}>
                <p style={{ margin:"0 0 8px", fontSize:11, color:"#475569",
                  fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
                  <strong style={{ color:"#64748B" }}>Disclaimer:</strong> This tool reflects state law as of 2026 and is provided for informational purposes only. Break laws change — always verify with your state's Department of Labor. This does not constitute legal advice. Consult a licensed employment attorney for compliance decisions.
                </p>
                <p style={{ margin:0, fontSize:11, color:"#475569",
                  fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
                  <strong style={{ color:"#64748B" }}>Automate compliance:</strong>{" "}
                  <a href="https://gusto.pxf.io/X4nKG5" target="_blank" rel="noopener noreferrer sponsored"
                    style={{ color:"#64748B", textDecoration:"underline" }}>Gusto</a>,{" "}
                  <a href="https://onpay.com/?ref=YOUR_ONPAY_ID" target="_blank" rel="noopener noreferrer sponsored"
                    style={{ color:"#64748B", textDecoration:"underline" }}>OnPay</a>, and{" "}
                  <a href="https://www.paychex.com" target="_blank" rel="noopener noreferrer"
                    style={{ color:"#64748B", textDecoration:"underline" }}>Paychex</a>{" "}
                  track break compliance automatically. Some links are affiliate links — we may earn a commission at no cost to you.
                </p>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}
